import { ProposalRow } from '@components/BookingProposal/ProposalRow';
import { ProposalRowsSkeleton } from '@components/BookingProposal/ProposalRowsSkeleton';
import { getProposalTypeLabel } from '@components/BookingProposal/proposalTypeLabel';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { plural, t } from '@lingui/core/macro';
import { cva } from 'noddi-ui-common';
import {
  NoddiBasicCard,
  NoddiButton,
  NoddiEmptyContent,
  NoddiLink,
  NoddiSearchBar,
  TablePagination,
  useNoddiQueryParam,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { customerRoutes, useDebounceValue } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useEffect, useState } from 'react';
import { StringParam, withDefault } from 'use-query-params';
import { usePendingBookingProposals, usePendingProposalTypeCounts } from '../../../hooks/usePendingBookingProposals';
import { getCommonTranslations } from '../../../utils/commonTranslations';

const PAGE_SIZE = 10;
/** Enough rows to hold the list's footprint while the first page loads. */
const SKELETON_ROW_COUNT = 5;

export const BookingProposals = () => {
  const { currentUserGroupId: userGroupId } = useAuthContext();

  if (!userGroupId) {
    return null;
  }

  return <PendingProposalList userGroupId={userGroupId} />;
};

const PendingProposalList = ({ userGroupId }: { userGroupId: number }) => {
  const { pagination, setPagination, search, setSearch } = usePaginationQueryParam({ initialPageSize: PAGE_SIZE });
  const [selectedType, setSelectedType] = useNoddiQueryParam('type', withDefault(StringParam, ''));
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebounceValue({ value: searchInput, delay: 400 });

  const { typeCounts } = usePendingProposalTypeCounts(userGroupId);
  // A hand-edited or stale `type` param must not leave the customer staring at an empty list.
  const activeType = typeCounts.find(({ type }) => type === selectedType)?.type ?? null;

  const { proposals, totalCount, error, isPending, isFetching } = usePendingBookingProposals(userGroupId, {
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    search,
    type: activeType
  });

  // Typing drives the URL — and with it the request — once it settles, from the first page.
  useEffect(() => {
    if (debouncedSearch !== search) {
      setSearch(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSearch is recreated on every render by the query-param hook
  }, [debouncedSearch]);

  const hasSearch = !!search;
  // Brand separation happens on the fetched page, so an empty page can outlive a non-zero count.
  const isEmpty = totalCount === 0 || proposals.length === 0;
  const allTypesCount = typeCounts.reduce((sum, { count }) => sum + count, 0);

  return (
    <ContentWrapperCustomerApp
      title={t`Bookings that need confirmation`}
      subtitle={
        isPending || isEmpty
          ? undefined
          : plural(totalCount, {
              one: '# vehicle is due. The time and the price are set up — confirm the booking.',
              other: '# vehicles are due. The time and the price are set up — confirm each booking.'
            })
      }
      backButtonPath={customerRoutes.homepage.getPath()}
      width='lg'
      error={error}
    >
      <div className='flex flex-col gap-4 md:gap-5'>
        <div className='md:max-w-sm'>
          <NoddiSearchBar
            defaultValue={search}
            handleChange={(event) => setSearchInput(event.target.value)}
            placeholder={t`Search license plate`}
          />
        </div>

        {typeCounts.length > 1 && (
          <div className='flex flex-wrap gap-2'>
            <FilterPill
              label={t`All`}
              count={allTypesCount}
              isActive={!activeType}
              onClick={() => setSelectedType('')}
            />
            {typeCounts.map(({ type, count }) => (
              <FilterPill
                key={type}
                label={getProposalTypeLabel(type)}
                count={count}
                isActive={activeType === type}
                onClick={() => setSelectedType(type)}
              />
            ))}
          </div>
        )}

        {isPending ? (
          <ProposalRowsSkeleton rowCount={SKELETON_ROW_COUNT} />
        ) : isEmpty ? (
          <NoddiEmptyContent
            emptyIcon={hasSearch ? 'Search' : 'Calendar'}
            title={hasSearch ? t`No matches` : t`Nothing to confirm`}
            message={
              hasSearch ? t`No bookings match that license plate.` : t`You have no suggested bookings right now.`
            }
          >
            {hasSearch ? (
              <NoddiButton
                variant='outline'
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                }}
              >
                {t`Clear search`}
              </NoddiButton>
            ) : (
              <NoddiLink to={customerRoutes.homepage.getPath()} variant='outline'>
                {t`Back to home`}
              </NoddiLink>
            )}
          </NoddiEmptyContent>
        ) : (
          <>
            <NoddiBasicCard className='flex flex-col divide-y divide-border overflow-hidden p-0'>
              {proposals.map((proposal) => (
                <ProposalRow key={proposal.id} proposal={proposal} />
              ))}
            </NoddiBasicCard>
            {totalCount > pagination.pageSize && (
              <NoddiBasicCard className='p-4'>
                <TablePagination
                  loading={isFetching}
                  pagination={pagination}
                  onChange={({ pageIndex, pageSize }) => setPagination({ pageIndex, pageSize, search })}
                  totalCount={totalCount}
                  translations={getCommonTranslations().table}
                />
              </NoddiBasicCard>
            )}
          </>
        )}
      </div>
    </ContentWrapperCustomerApp>
  );
};

const filterPillVariants = cva(
  'flex items-center gap-1.5 rounded-full border px-4 py-1 text-sm font-medium transition-colors',
  {
    variants: {
      isActive: {
        true: 'border-primary bg-primary text-primary-foreground',
        false: 'border-border bg-card text-foreground md:hover:bg-accent'
      }
    }
  }
);

type FilterPillProps = {
  label: string;
  count?: number;
  isActive: boolean;
  onClick: () => void;
};

const FilterPill = ({ label, count, isActive, onClick }: FilterPillProps) => (
  <button type='button' aria-pressed={isActive} onClick={onClick} className={filterPillVariants({ isActive })}>
    <span>{label}</span>
    {count != null && <span className='font-semibold tabular-nums'>{count}</span>}
  </button>
);
